import _ from 'lodash'
import debounce from 'lodash.debounce'
import '@/assets/styles/tooltip.css'
import { i18n } from '@/core/i18n'
import router from '@/router'
import { computed } from 'vue'
import { dectPasswordValidation } from '@/validations/validators'
import { getUpsellSubscriptionManagementUrl } from '@/utils/urls'
import { useFeaturesStore } from '@/stores/features/store'

const parseMarkdownLinks = (line: string) => {
  let copiedLine = line

  // Extended markdown link. It optionally allows to add CSS classes like this: "[text](link)<classes>"
  // There are some helper classes in @/assets/styles/tooltip.scss, to simplify the translations
  const linkPattern = /\[([^\]]*)\]\(([^)]*)\)(<([^>]*)>)?/

  while (copiedLine.match(linkPattern)) {
    const [original, text, link, , style] = copiedLine.match(linkPattern) ?? []
    const href =
      useFeaturesStore().hasSubscriptionManagementFeature &&
      /^\/(?:uca\/)?settings\?scroll_to=plan-change-settings$/.test(link)
        ? getUpsellSubscriptionManagementUrl()
        : link.startsWith('http')
          ? link
          : router.options.history.base + link
    const classes = style ?? 'eb-link'
    const aTag = `<a href="${href}" target="_blank" class="${classes}">${text}</a>`
    copiedLine = copiedLine.replace(original as string, aTag)
  }
  return copiedLine
}

const applyListStyles = (html: string) => {
  return html
    .replace(/<ul(.*?)>/g, (_match, attrs) => {
      if (attrs.includes('list-style-type')) {
        return `<ul${attrs}>`
      }
      return `<ul${attrs} style="list-style-type: disc; list-style-position: outside; padding-left: 1.25rem; margin: 0.5rem 0;">`
    })
    .replace(/<ol(.*?)>/g, (_match, attrs) => {
      if (attrs.includes('list-style-type')) {
        return `<ol${attrs}>`
      }
      return `<ol${attrs} style="list-style-type: decimal; list-style-position: outside; padding-left: 1.25rem; margin: 0.5rem 0;">`
    })
}

export const parseMarkdown = (string: string) => {
  const lines = string
    .replace(/\*\*(.*?)\*\*/g, '<b>$1</b>')
    .replace(/\\n/g, '\n')
    .split('\n')
  const htmlLines: string[] = []
  let isInList = false

  lines.forEach((line) => {
    // Bullet points
    if (line.match(/^\s*[-*]\s+/)) {
      if (!isInList) {
        htmlLines.push('<ul>')
        isInList = true
      }
      htmlLines.push(`<li>${line.replace(/^\s*[-*]\s+/, '')}</li>`)
      return
    }

    if (isInList) {
      htmlLines.push('</ul>')
      isInList = false
    }

    if (line === '') {
      htmlLines.push('<br />')
      return
    }

    // Preserve already-marked paragraph HTML from backend/i18n.
    if (line.trim().match(/^<p(?:\s[^>]*)?>.*<\/p>$/i)) {
      htmlLines.push(line)
      return
    }

    // Paragraph
    htmlLines.push(`<p>${line}</p>`)
  })

  if (isInList) {
    htmlLines.push('</ul>')
  }

  const html = htmlLines.map((line) => parseMarkdownLinks(line)).join('')
  return applyListStyles(html)
}

export function mx_combinedFilter<
  T extends Record<string, any>,
  K extends keyof T,
>(newData: T, originalData: T, allowedKeys: K[]) {
  const filteredData = mx_filterObjectByKeys(newData, allowedKeys)
  const changedData = mx_filterChangedData(filteredData, originalData)
  return changedData
}

export function mx_filterChangedData<T extends Record<string, any>>(
  newData: T,
  originalData: T
) {
  const filteredData: Partial<T> = {}
  for (const key in newData) {
    if (Array.isArray(newData[key])) {
      if (newData[key].length !== originalData[key].length) {
        filteredData[key] = newData[key]
      } else if (
        originalData[key].some(
          (originalEntry: string) =>
            !newData[key].some((newEntry: string) => newEntry === originalEntry)
        )
      ) {
        filteredData[key] = newData[key]
      }
    } else if (newData[key] !== originalData[key]) {
      filteredData[key] = newData[key]
    }
  }
  return filteredData
}

export function mx_filterObjectByKeys<
  T extends Record<string, any>,
  K extends keyof T,
>(objectToFilter: T, allowedKeys: K[]) {
  return _.omitBy(
    objectToFilter,
    (value, key) => !allowedKeys.includes(key as K)
  ) as Pick<T, K>
}

export function mx_existsAndIsNotEmpty(value: string | any[]) {
  return value && value.length > 0
}

export function mx_$pt(string: string, parameters?: any) {
  return computed(() => parseMarkdown(i18n.global.t(string, parameters)))
}

export function mx_$ps(string: string) {
  return parseMarkdown(string)
}

export async function mx_getIconSvgString(iconName: string) {
  async function getSvg(path: string) {
    try {
      const response = await fetch(path)

      if (!response.ok) {
        return ''
      }
      return response.text()
    } catch {
      return ''
    }
  }

  function getIconPath(iconName: string) {
    const fullIconName = iconName.includes('eb-')
      ? `eb/${iconName}`
      : `fontawesome/${iconName}`

    if (window.location.href.includes('gitlab.easybell.de')) {
      const gitlabPath = window.location.href
        .split('pages.gitlab.easybell.de/')[1]
        .split('/')[0]

      return `/${gitlabPath}/assets/icons/${fullIconName}.svg`
    }
    return `/assets/icons/${fullIconName}.svg`
  }
  const iconPath = getIconPath(iconName)
  const iconString = await getSvg(iconPath)
  const wrappedIconString = `<div style="width:1em;height:1em;margin-right:0.5em;">${iconString.replace(
    '<svg',
    '<svg style="width:100%;height:100%;fill:currentColor;"'
  )}</div>`
  return wrappedIconString
}

export const mx_delayInput = debounce(
  function (this: Record<string, any>, value: any, target: string) {
    this[target] = value
  },
  500,
  { leading: false }
)

export function generateValidPassword() {
  const lowerCase = 'abcdefghijklmnopqrstuvwxyz'
  const upperCase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const digits = '0123456789'
  // Backend validation: only a-Z, 0-9, and .:?!%&=*+~-_()[]{}
  const specialChars = '.:?!%&=*+~-_()[]{}'
  // THIS IS FROM SNOM'S MAGIC PASSWORD LIST
  const blacklist = [
    '1qazzwsx',
    'trustno1',
    'q1w2e3r4t5',
    'qlw2e3r4',
    '1q2w3e4r',
    'jordan23',
    'Password',
    'password1',
    '1q2w3e4r5t',
    'rush2112',
    'passwOrd',
    '1qazxsw2',
    'blink182',
    '12qwaszx',
    'qwerty12',
    'zaq12wsx',
    'ncc1701d',
    'michael1',
  ]

  function getRandomChar(str: string) {
    return str[Math.floor(Math.random() * str.length)]
  }

  function isValidPassword(password: string) {
    return (
      dectPasswordValidation(password) &&
      !blacklist.includes(password.toLowerCase())
    )
  }

  function generatePassword() {
    const allChars = lowerCase + upperCase + digits + specialChars
    let password = ''

    password += getRandomChar(lowerCase)
    password += getRandomChar(upperCase)
    password += getRandomChar(digits)
    password += getRandomChar(specialChars)

    while (password.length < 9) {
      password += getRandomChar(allChars)
    }

    password = password
      .split('')
      .sort(() => 0.5 - Math.random())
      .join('')

    return password
  }

  let newPassword
  do {
    newPassword = generatePassword()
  } while (!isValidPassword(newPassword))

  return newPassword
}

export function generateRandomNumber(maxLength: number) {
  const min = 10 ** (maxLength - 1)
  const max = 9 * min

  return Math.floor(min + Math.random() * max)
}
